import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import AdminChrome, {
  adminOutlineBtnSx,
  adminSolidBtnSx,
  adminSurfaceCardSx,
} from '../components/AdminChrome';
import { DOCUMENT } from '../constants/documentTheme';

const EMPTY_SHOW = {
  slug: '',
  name: '',
  path: '',
  source: '',
  datesLabel: '',
  venue: '',
  heroImageUrl: '',
  splitImageUrl: '',
  hubTitle: '',
  hubSubtitle: '',
  hubCta: 'Explore',
  heroEyebrow: '',
  heroTitle: 'Travel smarter. Partner smarter.',
  heroSubtitle: '',
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  agentsTitle: '',
  agentsSubtitle: '',
  interestTitle: '',
  interestSubtitle: '',
  interestSubmit: '',
  demoTitle: '',
  finalCtaSubtitle: '',
  isActive: true,
  showOnHub: true,
  sortOrder: 0,
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: DOCUMENT.ivory,
  },
};

const AdminMarketingShows = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [savingBanner, setSavingBanner] = useState(false);
  const [savingHub, setSavingHub] = useState(false);
  const [savingShow, setSavingShow] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [shows, setShows] = useState([]);
  const [banner, setBanner] = useState({
    enabled: false,
    message: '',
    messageShort: '',
    cta: '',
    href: '/shows',
  });
  const [hub, setHub] = useState({
    eyebrow: '',
    title: '',
    subtitle: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_SHOW);
  const [deleteId, setDeleteId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [showsRes, bannerRes, hubRes] = await Promise.all([
        api.get('/admin/marketing-shows/shows'),
        api.get('/admin/marketing-shows/banner'),
        api.get('/admin/marketing-shows/hub'),
      ]);
      setShows(showsRes.data?.shows || []);
      setBanner(bannerRes.data?.banner || banner);
      setHub(hubRes.data?.hub || hub);
    } catch (err) {
      setError(err.response?.data?.error || t('adminMarketing.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveBanner = async () => {
    setSavingBanner(true);
    setError('');
    setNotice('');
    try {
      const { data } = await api.put('/admin/marketing-shows/banner', banner);
      setBanner(data.banner);
      setNotice(t('adminMarketing.bannerSaved'));
    } catch (err) {
      setError(err.response?.data?.error || t('adminMarketing.saveError'));
    } finally {
      setSavingBanner(false);
    }
  };

  const saveHub = async () => {
    setSavingHub(true);
    setError('');
    setNotice('');
    try {
      const { data } = await api.put('/admin/marketing-shows/hub', hub);
      setHub(data.hub);
      setNotice(t('adminMarketing.hubSaved'));
    } catch (err) {
      setError(err.response?.data?.error || t('adminMarketing.saveError'));
    } finally {
      setSavingHub(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setDraft({ ...EMPTY_SHOW });
    setEditorOpen(true);
  };

  const openEdit = (show) => {
    setEditingId(show.id);
    setDraft({
      ...EMPTY_SHOW,
      ...show,
      isActive: Boolean(show.isActive),
      showOnHub: Boolean(show.showOnHub),
      sortOrder: Number(show.sortOrder) || 0,
    });
    setEditorOpen(true);
  };

  const updateDraft = (key, value) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'slug' && !editingId) {
        const slug = String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        if (!prev.path || prev.path === `/shows/${prev.slug || ''}`) {
          next.path = slug ? `/shows/${slug}` : '';
        }
        if (!prev.source || prev.source === prev.slug) {
          next.source = slug;
        }
        if (!prev.hubTitle) next.hubTitle = prev.name || slug;
      }
      if (key === 'name' && !editingId && !prev.hubTitle) {
        next.hubTitle = value;
      }
      return next;
    });
  };

  const saveShow = async () => {
    setSavingShow(true);
    setError('');
    setNotice('');
    try {
      if (editingId) {
        await api.put(`/admin/marketing-shows/shows/${editingId}`, draft);
        setNotice(t('adminMarketing.showUpdated'));
      } else {
        await api.post('/admin/marketing-shows/shows', draft);
        setNotice(t('adminMarketing.showCreated'));
      }
      setEditorOpen(false);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || t('adminMarketing.saveError'));
    } finally {
      setSavingShow(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setError('');
    setNotice('');
    try {
      await api.delete(`/admin/marketing-shows/shows/${deleteId}`);
      setDeleteId(null);
      setNotice(t('adminMarketing.showDeleted'));
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || t('adminMarketing.saveError'));
    }
  };

  const showFields = [
    ['name', t('adminMarketing.fields.name'), false],
    ['slug', t('adminMarketing.fields.slug'), false],
    ['path', t('adminMarketing.fields.path'), false],
    ['source', t('adminMarketing.fields.source'), false],
    ['datesLabel', t('adminMarketing.fields.dates'), false],
    ['venue', t('adminMarketing.fields.venue'), false],
    ['heroImageUrl', t('adminMarketing.fields.heroImage'), false],
    ['splitImageUrl', t('adminMarketing.fields.splitImage'), false],
    ['hubTitle', t('adminMarketing.fields.hubTitle'), false],
    ['hubSubtitle', t('adminMarketing.fields.hubSubtitle'), true],
    ['hubCta', t('adminMarketing.fields.hubCta'), false],
    ['heroEyebrow', t('adminMarketing.fields.heroEyebrow'), false],
    ['heroTitle', t('adminMarketing.fields.heroTitle'), false],
    ['heroSubtitle', t('adminMarketing.fields.heroSubtitle'), true],
    ['seoTitle', t('adminMarketing.fields.seoTitle'), false],
    ['seoDescription', t('adminMarketing.fields.seoDescription'), true],
    ['seoKeywords', t('adminMarketing.fields.seoKeywords'), false],
    ['agentsTitle', t('adminMarketing.fields.agentsTitle'), false],
    ['agentsSubtitle', t('adminMarketing.fields.agentsSubtitle'), true],
    ['interestTitle', t('adminMarketing.fields.interestTitle'), false],
    ['interestSubtitle', t('adminMarketing.fields.interestSubtitle'), true],
    ['interestSubmit', t('adminMarketing.fields.interestSubmit'), false],
    ['demoTitle', t('adminMarketing.fields.demoTitle'), false],
    ['finalCtaSubtitle', t('adminMarketing.fields.finalCtaSubtitle'), true],
  ];

  return (
    <AdminChrome
      title={t('adminMarketing.title')}
      subtitle={t('adminMarketing.subtitle')}
      actions={(
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadAll}
          disabled={loading}
          sx={adminOutlineBtnSx}
        >
          {t('adminMarketing.refresh')}
        </Button>
      )}
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Card sx={adminSurfaceCardSx}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, mb: 1 }}
              >
                {t('adminMarketing.bannerSection')}
              </Typography>
              <Typography sx={{ color: DOCUMENT.ink3, mb: 2, fontSize: '0.9rem' }}>
                {t('adminMarketing.bannerHelp')}
              </Typography>
              <FormControlLabel
                sx={{ mb: 2, ml: 0 }}
                control={(
                  <Switch
                    checked={Boolean(banner.enabled)}
                    onChange={(e) => setBanner((prev) => ({ ...prev, enabled: e.target.checked }))}
                    inputProps={{ 'data-testid': 'admin-banner-enabled' }}
                  />
                )}
                label={t('adminMarketing.bannerEnabled')}
              />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('adminMarketing.fields.bannerMessage')}
                    value={banner.message || ''}
                    onChange={(e) => setBanner((prev) => ({ ...prev, message: e.target.value }))}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('adminMarketing.fields.bannerMessageShort')}
                    value={banner.messageShort || ''}
                    onChange={(e) => setBanner((prev) => ({ ...prev, messageShort: e.target.value }))}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('adminMarketing.fields.bannerCta')}
                    value={banner.cta || ''}
                    onChange={(e) => setBanner((prev) => ({ ...prev, cta: e.target.value }))}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('adminMarketing.fields.bannerHref')}
                    value={banner.href || ''}
                    onChange={(e) => setBanner((prev) => ({ ...prev, href: e.target.value }))}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<SaveIcon />}
                  onClick={saveBanner}
                  disabled={savingBanner}
                  sx={adminSolidBtnSx}
                >
                  {savingBanner ? t('adminMarketing.saving') : t('adminMarketing.saveBanner')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={adminSurfaceCardSx}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, mb: 1 }}
              >
                {t('adminMarketing.hubSection')}
              </Typography>
              <Grid container spacing={2}>
                {[
                  ['eyebrow', t('adminMarketing.fields.hubEyebrow')],
                  ['title', t('adminMarketing.fields.hubPageTitle')],
                  ['subtitle', t('adminMarketing.fields.hubPageSubtitle')],
                  ['seoTitle', t('adminMarketing.fields.seoTitle')],
                  ['seoDescription', t('adminMarketing.fields.seoDescription')],
                  ['seoKeywords', t('adminMarketing.fields.seoKeywords')],
                ].map(([key, label]) => (
                  <Grid item xs={12} md={key === 'subtitle' || key === 'seoDescription' ? 12 : 6} key={key}>
                    <TextField
                      fullWidth
                      multiline={key === 'subtitle' || key === 'seoDescription'}
                      minRows={key === 'subtitle' || key === 'seoDescription' ? 2 : 1}
                      label={label}
                      value={hub[key] || ''}
                      onChange={(e) => setHub((prev) => ({ ...prev, [key]: e.target.value }))}
                      sx={fieldSx}
                    />
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<SaveIcon />}
                  onClick={saveHub}
                  disabled={savingHub}
                  sx={adminSolidBtnSx}
                >
                  {savingHub ? t('adminMarketing.saving') : t('adminMarketing.saveHub')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={adminSurfaceCardSx}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontFamily: DOCUMENT.display, fontWeight: 600 }}
                >
                  {t('adminMarketing.showsSection')}
                </Typography>
                <Button startIcon={<AddIcon />} onClick={openCreate} sx={adminSolidBtnSx}>
                  {t('adminMarketing.addShow')}
                </Button>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('adminMarketing.fields.name')}</TableCell>
                      <TableCell>{t('adminMarketing.fields.path')}</TableCell>
                      <TableCell>{t('adminMarketing.fields.dates')}</TableCell>
                      <TableCell align="center">{t('adminMarketing.fields.active')}</TableCell>
                      <TableCell align="center">{t('adminMarketing.fields.onHub')}</TableCell>
                      <TableCell align="right">{t('adminMarketing.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography color="text.secondary">
                            {t('adminMarketing.noShows')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      shows.map((show) => (
                        <TableRow key={show.id} hover>
                          <TableCell>{show.name}</TableCell>
                          <TableCell>
                            <Typography component="code" sx={{ fontSize: '0.8rem' }}>
                              {show.path}
                            </Typography>
                          </TableCell>
                          <TableCell>{show.datesLabel || '—'}</TableCell>
                          <TableCell align="center">{show.isActive ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="center">{show.showOnHub ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => openEdit(show)}
                              sx={{ ...adminOutlineBtnSx, mr: 1, py: 0.25 }}
                            >
                              {t('adminMarketing.edit')}
                            </Button>
                            <Button
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => setDeleteId(show.id)}
                              sx={{ ...adminOutlineBtnSx, py: 0.25, color: DOCUMENT.oxblood }}
                            >
                              {t('adminMarketing.delete')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Stack>
      )}

      <Dialog
        open={editorOpen}
        onClose={() => !savingShow && setEditorOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 0, border: `1px solid ${DOCUMENT.rule}` } }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>
          {editingId ? t('adminMarketing.editShow') : t('adminMarketing.addShow')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {showFields.map(([key, label, multiline]) => (
              <Grid item xs={12} md={multiline ? 12 : 6} key={key}>
                <TextField
                  fullWidth
                  label={label}
                  value={draft[key] || ''}
                  onChange={(e) => updateDraft(key, e.target.value)}
                  multiline={multiline}
                  minRows={multiline ? 2 : 1}
                  sx={fieldSx}
                />
              </Grid>
            ))}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={t('adminMarketing.fields.sortOrder')}
                value={draft.sortOrder}
                onChange={(e) => updateDraft('sortOrder', Number(e.target.value))}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(draft.isActive)}
                    onChange={(e) => updateDraft('isActive', e.target.checked)}
                  />
                )}
                label={t('adminMarketing.fields.active')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(draft.showOnHub)}
                    onChange={(e) => updateDraft('showOnHub', e.target.checked)}
                  />
                )}
                label={t('adminMarketing.fields.onHub')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditorOpen(false)} disabled={savingShow} sx={adminOutlineBtnSx}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={saveShow}
            disabled={savingShow || !draft.name || !draft.slug}
            startIcon={<SaveIcon />}
            sx={adminSolidBtnSx}
          >
            {savingShow ? t('adminMarketing.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        PaperProps={{ sx: { borderRadius: 0, border: `1px solid ${DOCUMENT.rule}` } }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>
          {t('adminMarketing.deleteShow')}
        </DialogTitle>
        <DialogContent>
          <Typography>{t('adminMarketing.deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={adminOutlineBtnSx}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} sx={{ ...adminSolidBtnSx, bgcolor: DOCUMENT.oxblood }}>
            {t('adminMarketing.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminChrome>
  );
};

export default AdminMarketingShows;
